import { auth } from "./firebase";

async function authHeader(): Promise<HeadersInit> {
  const u = auth.currentUser;
  if (!u) throw new Error("not_signed_in");
  const token = await u.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function jsonRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  const authH = await authHeader();
  for (const [k, v] of Object.entries(authH)) headers.set(k, v as string);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    const text = await res.text();
    let pretty = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed.error === "string") pretty = parsed.error;
    } catch {
      // not JSON
    }
    throw new Error(`${res.status} ${pretty}`);
  }
  return (await res.json()) as T;
}

export const api = {
  me: () =>
    jsonRequest<{
      user: { uid: string; email: string; name: string; picture: string | null };
      isAdmin: boolean;
    }>("/api/me"),

  instantiateChecklist: (dealId: string, templateId?: string) =>
    jsonRequest<{ dealId: string; itemCount: number }>(
      `/api/checklists/${encodeURIComponent(dealId)}/instantiate`,
      { method: "POST", body: JSON.stringify(templateId ? { templateId } : {}) },
    ),

  deleteChecklist: (dealId: string) =>
    jsonRequest<{ ok: true }>(`/api/checklists/${encodeURIComponent(dealId)}`, {
      method: "DELETE",
    }),

  /**
   * Two-step upload: sign → PUT straight to GCS → complete. Bytes never
   * touch the Railway server (its proxy kills large uploads).
   */
  uploadDoc: async (
    dealId: string,
    file: File,
    kind: import("@closing/shared").ClosingDocKind = "deal_doc",
  ): Promise<{ docId: string }> => {
    const sign = await jsonRequest<{
      docId: string;
      uploadUrl: string;
      uploadToken: string;
      uploadHeaders: Record<string, string>;
    }>(`/api/uploads/${encodeURIComponent(dealId)}/sign`, {
      method: "POST",
      body: JSON.stringify({
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        kind,
      }),
    });
    // Headers must match what the backend signed, byte-for-byte.
    const putRes = await fetch(sign.uploadUrl, {
      method: "PUT",
      headers: sign.uploadHeaders,
      body: file,
    });
    if (!putRes.ok) {
      const body = await putRes.text().catch(() => "");
      throw new Error(`upload_failed ${putRes.status}: ${body.slice(0, 200)}`);
    }
    return jsonRequest<{ docId: string }>(`/api/uploads/complete`, {
      method: "POST",
      body: JSON.stringify({ uploadToken: sign.uploadToken }),
    });
  },

  getDocUrl: (docId: string) =>
    jsonRequest<{ url: string; filename: string }>(
      `/api/uploads/${encodeURIComponent(docId)}/url`,
    ),

  deleteDoc: (docId: string) =>
    jsonRequest<{ ok: true }>(`/api/uploads/${encodeURIComponent(docId)}`, { method: "DELETE" }),

  recommend: (dealId: string, docIds?: string[]) =>
    jsonRequest<{ runId: string }>(`/api/recommend/${encodeURIComponent(dealId)}`, {
      method: "POST",
      body: JSON.stringify(docIds?.length ? { docIds } : {}),
    }),
};
